import {
    useState,
    type RefObject,
    useEffect,
    createRef,
    useRef,
    useMemo,
    useCallback
} from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { getColor } from "../../../../libraries/wpl/foundations/src/adg-colors";

type CardProps = {
    label: string;
    title: string;
    body: string;
    contentSrc: string;
};

const hexAlpha30 = "4D"; // 30% alpha as hex

const backgroundColorMapping = {
    lightblue: "b50",
    white: "n0",
    darkblue: "n900",
    blue: "b500",
    purple: "p500"
};

const cardActiveBackgroundMapping = {
    lightblue: "n0",
    white: "n0",
    darkblue: "n0",
    blue: "n0",
    purple: "n0"
};

const cardHoverBackgroundMapping = {
    lightblue: "n0",
    white: "n0",
    darkblue: "n0",
    blue: "n0",
    purple: "n0"
};

const headerColorMapping = {
    lightblue: "n700",
    white: "n700",
    darkblue: "n0",
    blue: "n0",
    purple: "n0"
};

const textColorMapping = {
    lightblue: "n900",
    white: "n900",
    darkblue: "n0",
    blue: "n0",
    purple: "n0"
};

const labelColorMapping = {
    lightblue: "n400",
    white: "n400",
    darkblue: "n0",
    blue: "n0",
    purple: "n0"
};

type FeatureSectionProps = {
    header?: string;
    layout?: "cardleft" | "cardright";
    background: keyof typeof backgroundColorMapping;
    barColor?: "y200" | "r200" | "t200" | "g200" | "b200" | "p100";
    cards: CardProps[];
};

const wrapperCss = css`
    height: fit-content;
    margin: 0 auto;
    max-width: 1200px;
    padding: 0 ${token("space.200")};
`;

const rowCss = css`
    display: flex;
    gap: ${token("space.200")};

    ${UNSAFE_media.below.md} {
        flex-direction: column;
    }
`;

const contentsWrapperCss = css`
    flex: 2;
    overflow: hidden;
`;

const FeatureHighlight = (props: FeatureSectionProps) => {
    const { background, barColor } = props;
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [isVideoInView, setIsVideoInView] = useState<boolean>(false);

    const videoRefMap = useMemo(
        () => new Map<number, RefObject<HTMLVideoElement>>(),
        []
    );
    const videoWrapperRef = useRef(null);

    const sectionCss = css`
        background: ${getColor(backgroundColorMapping[background])};
        overflow: hidden;
    `;

    const headerCss = css`
        color: ${getColor(headerColorMapping[background])};
        font-size: 36px;
        font-weight: 500;
        line-height: 44px;
        margin: 0 auto;
        max-width: calc(66.6666% - ${token("space.200")});
        padding-bottom: ${token("space.400")};
        padding-top: ${token("space.800")};
        text-align: center;

        ${UNSAFE_media.below.md} {
            padding-top: ${token("space.400")};
        }

        ${UNSAFE_media.below.sm} {
            font-size: ${token("space.300")};
            line-height: ${token("space.400")};
            max-width: max-content;
            padding-top: ${token("space.400")};
        }
    `;

    const cardsWrapperCss = css`
        flex: 1;

        ${UNSAFE_media.only.sm} {
            display: flex;
            gap: ${token("space.400")};
        }

        ${UNSAFE_media.below.sm} {
            display: inline-flex;
            overflow: visible;
            transition: transform 0.3s;
            transform: translateX(-${selectedIndex * 100}%);
            white-space: nowrap;
        }
    `;

    const cardCss = css`
        display: inline-flex;
        flex-direction: column;
        flex-grow: 1;
        appearance: none;
        border: 0;
        background: transparent;
        border-left: ${barColor
            ? `${token("space.100")} solid transparent`
            : "none"};
        color: ${getColor(textColorMapping[background])};
        cursor: pointer;
        font-family: var(--bxp-charlie-text-font-family);
        margin-bottom: ${token("space.100")};
        padding: ${token("space.200")};
        text-align: left;
        width: 100%;

        ${UNSAFE_media.below.sm} {
            border-left: none;
            white-space: normal;
            min-width: 100%;
            border-top: ${barColor
                ? `${token("space.100")} solid transparent`
                : "none"};
        }

        ${UNSAFE_media.only.sm} {
            border-left: none;
            border-top: ${barColor
                ? `${token("space.100")} solid transparent`
                : "none"};
            margin: 0;
        }

        &:hover {
            background: ${getColor(cardHoverBackgroundMapping[background]) +
            hexAlpha30};
            border-left: ${barColor
                ? `${token("space.100")} solid ${getColor(barColor)}`
                : "none"};

            ${UNSAFE_media.below.md} {
                border-left: none;
                border-top: ${barColor
                    ? `${token("space.100")} solid ${getColor(barColor)}`
                    : "none"};
            }
        }

        > h3 {
            color: ${getColor(labelColorMapping[background])};
            display: block;
            font-size: ${token("space.150")};
            font-weight: 400;
            line-height: 18px;
            padding-bottom: ${token("space.200")};
            margin: 0;
            text-transform: uppercase;
        }

        > h4 {
            font-family: var(--bxp-charlie-text-font-family);
            font-size: ${token("space.250")};
            font-weight: 500;
            line-height: ${token("space.250")};
            letter-spacing: 0.3px;
            margin: 0;
            padding-bottom: ${token("space.100")};

            ${UNSAFE_media.below.md} {
                font-size: ${token("space.200")};
            }
        }

        > p {
            font-size: ${token("space.200")};
            font-weight: 400;
            line-height: ${token("space.300")};
            margin: 0;
        }
    `;

    const selectedCardCss = css`
        background: ${getColor(
            cardActiveBackgroundMapping[background]
        )} !important;
        border-left: ${barColor
            ? `${token("space.100")} solid ${getColor(barColor)}`
            : "none"};

        ${UNSAFE_media.below.md} {
            border-left: none;
            border-top: ${barColor
                ? `${token("space.100")} solid ${getColor(barColor)}`
                : "none"};
        }
    `;

    const contentsSliderCss = css`
        transform: translateX(-${selectedIndex * 100}%);
        white-space: nowrap;

        ${UNSAFE_media.below.sm} {
            transition: transform 0.3s;
        }
    `;

    const mobileDotsCss = css`
        display: none;

        ${UNSAFE_media.below.sm} {
            display: flex;
            gap: ${token("space.150")};
            justify-content: center;

            > button {
                appearance: none;
                background: ${getColor(
                    cardActiveBackgroundMapping[background]
                )};
                border: 0;
                border-radius: 50%;
                padding: 0;
                height: ${token("space.100")};
                width: ${token("space.100")};

                &.active {
                    background: ${getColor(textColorMapping[background])};
                }
            }
        }
    `;

    const resetSelectedIndexVideo = useCallback(() => {
        const video = videoRefMap.get(selectedIndex);
        if (video?.current) {
            video.current.currentTime = 0;
        }
    }, [selectedIndex, videoRefMap]);

    const resetVideoAndSetSelectedIndex = (index: number) => {
        resetSelectedIndexVideo();
        setSelectedIndex(index);
    };

    const nextCard = () => {
        const nextIndex = selectedIndex + 1;
        if (nextIndex === cards.length) {
            return;
        }

        resetVideoAndSetSelectedIndex(nextIndex);
    };

    const previousCard = () => {
        const nextIndex = selectedIndex - 1;
        if (nextIndex === -1) {
            return;
        }

        resetVideoAndSetSelectedIndex(nextIndex);
    };

    const cards = props.cards.map((card, index) => (
        <button
            key={card.title}
            css={[cardCss, selectedIndex === index && selectedCardCss]}
            onClick={() => {
                if (index !== selectedIndex)
                    resetVideoAndSetSelectedIndex(index);
            }}
            tabIndex={0}
        >
            {card.label && <h3>{card.label}</h3>}
            <h4>{card.title}</h4>
            <p>{card.body}</p>
        </button>
    ));

    const contents = props.cards.map((card, index) => {
        const ref = createRef<HTMLVideoElement>();

        videoRefMap.set(index, ref);

        return (
            <video
                key={`${card.title}-content`}
                ref={ref}
                width="100%"
                height="100%"
                muted={true}
                // eslint-disable-next-line react/no-unknown-property
                object-fit="fill"
                preload="auto"
                tabIndex={-1}
                onEnded={() => {
                    selectedIndex + 1 === cards.length
                        ? resetVideoAndSetSelectedIndex(0)
                        : nextCard();
                }}
                playsInline={true}
            >
                <source src={card.contentSrc} />
            </video>
        );
    });

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);

    const startDrag = (
        touchEvent?: React.TouchEvent,
        mouseEvent?: React.MouseEvent
    ) => {
        setIsDragging(true);
        setStartX(
            (touchEvent && touchEvent.touches[0]
                ? touchEvent?.touches[0].clientX
                : mouseEvent?.clientX) || 0
        );
    };

    const stopDrag = (
        touchEvent?: React.TouchEvent,
        mouseEvent?: React.MouseEvent
    ) => {
        if (isDragging) {
            setIsDragging(false);

            let dragAmount = 0;
            if (touchEvent && touchEvent.changedTouches[0]) {
                dragAmount = touchEvent.changedTouches[0].clientX - startX;
            } else if (mouseEvent) {
                dragAmount = mouseEvent.clientX - startX;
            }

            if (Math.abs(dragAmount) > 100) {
                if (dragAmount < 0) {
                    nextCard(); // Swipe left
                } else {
                    previousCard(); // Swipe right
                }
            }
        }
    };

    useEffect(() => {
        const handleIntersect = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setIsVideoInView(true);
                } else {
                    setIsVideoInView(false);
                    resetSelectedIndexVideo();
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersect, {
            threshold: 0.3
        });

        const ref = videoWrapperRef.current;

        if (ref) {
            observer.observe(ref);
        }

        return () => {
            if (ref) {
                observer.unobserve(ref);
            }
        };
    }, [videoWrapperRef, resetSelectedIndexVideo]);

    useEffect(() => {
        if (isDragging) {
            return;
        }

        for (const [videoId, video] of videoRefMap.entries()) {
            if (!video?.current || !video.current.play) {
                continue;
            }
            if (videoId === selectedIndex && isVideoInView) {
                video.current.play();
            } else {
                video.current.pause();
            }
        }
    }, [selectedIndex, isVideoInView, isDragging, videoRefMap]);

    return (
        <section css={sectionCss}>
            <div css={wrapperCss}>
                {props.header && <h2 css={headerCss}>{props.header}</h2>}
                <div
                    css={rowCss}
                    onTouchStart={(e) => startDrag(e)}
                    onMouseDown={(e) => startDrag(undefined, e)}
                    onTouchEnd={(e) => stopDrag(e)}
                    onMouseUp={(e) => stopDrag(undefined, e)}
                >
                    {props.layout !== "cardright" && (
                        <div css={cardsWrapperCss}>{cards}</div>
                    )}
                    <div ref={videoWrapperRef} css={contentsWrapperCss}>
                        <div css={contentsSliderCss}>{contents}</div>
                    </div>
                    {props.layout === "cardright" && (
                        <div css={cardsWrapperCss}>{cards}</div>
                    )}
                </div>
                <div
                    css={css`
                        padding: ${token("space.400")} 0;
                    `}
                >
                    <div css={mobileDotsCss}>
                        {props.cards.map((card, index) => (
                            <button
                                key={`${card.title}-mobile-dot`}
                                onClick={() => {
                                    if (index !== selectedIndex)
                                        resetVideoAndSetSelectedIndex(index);
                                }}
                                className={
                                    index === selectedIndex ? "active" : ""
                                }
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeatureHighlight;
